import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as mobx from 'mobx';
import * as mobxReact from 'mobx-react';
import classnames from 'classnames';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { ReportConfigurationTarget, IReportConfigItem, IReportConfiguration, IConfigurationFilter, ReportLayout } from '../../../../../../api/reports.dao';
import { Popover } from '../../../../../global/popover/popover';
import DefaultButton from '../../../../../global/default-button/default-button';
import Text from '../../../../../global/text/text';
import { SearchInput } from '../../../../../global/search-input/search-input';
import './_saved-configurations.scss';
import CollapseSection from '../../../../../global/collapse-section/collapse-section';
import SavedItem from '../../../../../global/_saved-item/_saved-item';
import { ReportConfigurationViewModel } from '../report-configuration.viewmodel';
import { ReportConfigurationPresentation } from '../report-configuration-modal';
import { LOADING } from '../../../../../../constants/common-labels.constants';

export interface IProps {
    intl: IntlShape;
    configurationVm: ReportConfigurationViewModel;
}

const SavedConfigurations = injectIntl(mobxReact.observer((props: IProps) => {
    const { intl, configurationVm } = props;

    const {
        eventColumns,
        currentConfiguration,
        configurations,
        resetConfiguration,
        onSelectConfiguration,
        getSavedConfigurations,
        onConfigurationsSelectionChange,
        deleteConfiguration,
        isAddingNewModalOpen,
        setAddingNewModalOpen,
        appliedFilters,
        availableFilterTypes,
        getAppliedFiltersForSave,
        getAvailableColumns,
        layoutType,
        layoutSpecificFields
    } = configurationVm;

    const [isConfigurationsOpen, setIsConfigurationsOpen] = useState(false);
    const [filterValue, setFilterValue] = useState('');
    const configurationsRef = useRef();

    const filteredConfigurations = useMemo(() => {
        return configurations?.filter(el => {
            return el.name.toLocaleLowerCase().includes(filterValue.toLocaleLowerCase());
        }) || [];
    }, [configurations, filterValue]);

    const userConfigurations = useMemo(() => configurations?.filter(el =>
        el.target === ReportConfigurationTarget.USER), [configurations]);
    const accountConfigurations = useMemo(() => configurations?.filter(el =>
        el.target === ReportConfigurationTarget.ACCOUNT), [configurations]);

    const filteredUserConfigurations = useMemo(() => filteredConfigurations.filter(el =>
        el.target === ReportConfigurationTarget.USER), [filteredConfigurations]);
    const filteredAccountConfigurations = useMemo(() => filteredConfigurations.filter(el =>
        el.target === ReportConfigurationTarget.ACCOUNT), [filteredConfigurations]);

    const currentEnabledColumns = useMemo(
        () => getAvailableColumns().filter(el => el.enabled && !el.customField),
        [currentConfiguration, layoutSpecificFields]
    );

    const handleToggleConfigurations = () => {
        setIsConfigurationsOpen(prev => !prev);
    };

    const handleSelectClick = (configuration: IReportConfiguration) => {
        setIsConfigurationsOpen(false);
        onSelectConfiguration(configuration.columns, configuration.filters, configuration.layoutType);
    };

    const isLayoutsEquals = useCallback((configLayout: ReportLayout = ReportLayout.ROW_PER_ATTENDEE) => {
        return configLayout === layoutType;
    }, [layoutType]);

    const isCurrentColumnsEquals = useCallback((columns: IReportConfigItem[]) => {
        const enabledColumns = columns.filter(el => el.enabled && !el.customField && layoutSpecificFields.find(field => el.fieldId === field.fieldId));

        let result;

        if (currentEnabledColumns.length === enabledColumns.length) {
            result = true;

            for (let i = 0; i < currentEnabledColumns.length; i++) {
                if (currentEnabledColumns[i].fieldId !== enabledColumns[i].fieldId) {
                    result = false;
                    break;
                }
            }
        } else {
            result = false;
        }

        return result;
    }, [currentEnabledColumns]);

    const compareArrays = useCallback((a, b) => {
        if (a.length === b.length) {
            const lowerCasedA = a.map(el => el.toLowerCase());
            const lowerCasedB = b.map(el => el.toLowerCase());

            return lowerCasedA.every(el => lowerCasedB.includes(el));
        }
    }, []);

    const isCurrentFiltersEquals = useCallback((filters: IConfigurationFilter[] = []) => {
        let result;

        const appliedFiltersForSave = getAppliedFiltersForSave();
        if (appliedFiltersForSave.length === filters.length) {
            result = true;

            for (const currentAppliedFilter of appliedFiltersForSave) {
                const currentFilter = filters.find(filter => filter.field === currentAppliedFilter.field);

                if (!currentFilter ||
                    currentAppliedFilter.operator !== currentFilter.operator ||
                    !compareArrays(currentAppliedFilter.values, currentFilter.values)
                ) {
                    result = false;
                    break;
                }
            }
        } else {
            result = false;
        }

        return result;
    }, [appliedFilters, getAppliedFiltersForSave, compareArrays]);

    const findConfiguration = useCallback((configs?: IReportConfiguration[]): IReportConfiguration | undefined => {
        let result: IReportConfiguration | undefined;

        configs?.some(config => {
            if (isLayoutsEquals(config.layoutType) && isCurrentColumnsEquals(config.columns) && isCurrentFiltersEquals(config.filters)) {
                result = config;
                return true;
            }
        });

        return result;
    }, [isCurrentColumnsEquals, isCurrentFiltersEquals, isLayoutsEquals]);


    const getSelectedConfigurations = useCallback((): IReportConfiguration[] => {
        const userConfig = findConfiguration(userConfigurations);
        const accountConfig = findConfiguration(accountConfigurations);

        return [accountConfig, userConfig].filter(Boolean);
    }, [userConfigurations, accountConfigurations, findConfiguration]);

    const selectedConfigurations: IReportConfiguration[] = useMemo(() => getSelectedConfigurations(), [getSelectedConfigurations]);
    const isDefaultConfiguration = useMemo(() => isCurrentColumnsEquals(eventColumns), [isCurrentColumnsEquals, eventColumns]);

    useEffect(() => {
        getSavedConfigurations();
    }, []);

    useEffect(() => {
        if (isConfigurationsOpen) getSavedConfigurations();
    }, [isConfigurationsOpen]);

    useEffect(() => {
        onConfigurationsSelectionChange(selectedConfigurations);
    }, [selectedConfigurations]);

    return (
        <div className='saved-configurations'>
            {!isDefaultConfiguration && <DefaultButton
                className='saved-configurations__reset'
                text={intl.formatMessage({
                    id: 'oe.common.reset',
                    defaultMessage: 'Reset',
                    description: 'Button text'
                })}
                modifier={['link', 'confirm-safe']}
                onClick={resetConfiguration}
            />}
            <div
                className={classnames('saved-configurations__modal-button', {
                    'saved-configurations__modal-button--selected': isConfigurationsOpen || selectedConfigurations.length
                })}
                onClick={handleToggleConfigurations}
                ref={configurationsRef}
            >
                <>
                    <div className='saved-filters__view-button-text' title={selectedConfigurations[0]?.name}>
                        <div>
                            {intl.formatMessage(
                                {
                                    id: 'oe.reports.savedConfiguration',
                                    description: 'Saved configurations button',
                                    defaultMessage: 'Saved Configurations',
                                }
                            )}
                        </div>
                        {!!selectedConfigurations.length && <Text tag='span' message={`: ${selectedConfigurations[0].name}`} withEllipsis withoutTitle />}
                    </div>
                    <Popover
                        isOpen={isConfigurationsOpen}
                        buttonRef={configurationsRef}
                        onClickOutside={() => setIsConfigurationsOpen(false)}
                        customWidth={300}
                        padding={0}
                        align='end'
                        position='bottom'
                    >
                        <div className='w-100 saved-filters__container' onClick={e => e.stopPropagation()}>
                            <div className='saved-configurations__search'>
                                <SearchInput
                                    placeholder={intl.formatMessage(
                                        {
                                            id: 'oe.common.search',
                                            description: 'Search configurations placeholder',
                                            defaultMessage: 'Search',
                                        }
                                    )}
                                    onChange={(e, value) => setFilterValue(value)}
                                    value={filterValue}
                                />
                            </div>
                            {!configurations && <div className='saved-configurations__loading'>
                                <FormattedMessage
                                    {...LOADING}
                                    values={{
                                        suffix: '...'
                                    }}
                                />
                            </div>}
                            <div className='saved-configurations__content'>
                                <div
                                    onClick={mobx.action(setAddingNewModalOpen)}
                                    className='saved-filters__save-button'
                                >
                                    <div className='saved-filters__save-button-label'>
                                        {intl.formatMessage(
                                            {
                                                id: 'oe.reports.saveConfiguration',
                                                description: 'Save configuration button',
                                                defaultMessage: 'Save Configuration',
                                            }
                                        )}
                                    </div>
                                </div>
                                {configurations && !configurations.length && (
                                    <div className='saved-configurations__no-content'>
                                        {intl.formatMessage(
                                            {
                                                id: 'oe.reports.youDontHaveAnySavedConfigurationYet',
                                                description: 'No saved configurations placeholder',
                                                defaultMessage: `You don't have any saved configurations yet.`,
                                            }
                                        )}
                                    </div>
                                )}
                                {!!filteredUserConfigurations.length &&
                                    <CollapseSection
                                        key={ReportConfigurationTarget.USER}
                                        title={intl.formatMessage({
                                            id: 'oe.reports.myConfigurations',
                                            description: 'Section title',
                                            defaultMessage: 'My Configurations',
                                        })
                                        }>
                                        {filteredUserConfigurations.map(configuration => (
                                            <SavedItem
                                                key={configuration.reportConfigurationTemplateId}
                                                name={configuration.name}
                                                isSelected={selectedConfigurations.includes(configuration)}
                                                onSelect={() => handleSelectClick(configuration)}
                                                onDelete={() => deleteConfiguration(configuration.reportConfigurationTemplateId)}
                                            />
                                        ))}
                                    </CollapseSection>}
                                {!!filteredAccountConfigurations.length &&
                                    <CollapseSection
                                        key={ReportConfigurationTarget.ACCOUNT}
                                        title={intl.formatMessage({
                                            id: 'oe.reports.accountConfigurations',
                                            description: 'Section title',
                                            defaultMessage: 'Account Configurations',
                                        })
                                        }>
                                        {filteredAccountConfigurations.map(configuration => (
                                            <SavedItem
                                                key={configuration.reportConfigurationTemplateId}
                                                name={configuration.name}
                                                isSelected={selectedConfigurations.includes(configuration)}
                                                onSelect={() => handleSelectClick(configuration)}
                                                onDelete={() => deleteConfiguration(configuration.reportConfigurationTemplateId)}
                                            />
                                        ))}
                                    </CollapseSection>}
                            </div>
                            {!!configurations?.length && !filteredConfigurations.length && (
                                <div className='saved-configurations__empty-search'>
                                    <FormattedMessage
                                        id='oe.reports.noConfigurationsFound'
                                        defaultMessage='No configurations found.'
                                        description='Empty search placeholder'
                                    />
                                </div>
                            )}
                        </div>
                    </Popover>
                </>
            </div>
            {isAddingNewModalOpen &&
                <ReportConfigurationPresentation
                    vm={configurationVm}
                    availableFilterTypes={availableFilterTypes}
                />
            }
        </div>);
}));

export default SavedConfigurations;
